import React, { useEffect, useMemo, useState } from "react";
import {
  IonCardContent,
  IonContent,
  IonPage,
  useIonRouter,
} from "@ionic/react";
import Header from "../../../components/Header";
import { fetchDocumentByType } from "../../../services/documents";
import { Preferences } from "@capacitor/preferences";

interface TermsOfUseDocument {
  id: number;
  type: string;
  version: string;
  effective_date: string;
  last_updated: string;
  title: string;
  content_markdown: string;
  content_html: string;
  is_current: boolean;
  created_at: string;
  updated_at: string;
}

const TERMS_OF_USE_KEY = "termsOfUse";

/**
 * ✅ Reusable Terms of Use Content Renderer
 * Can be used independently in modals, onboarding, or other pages.
 */
export const TermsOfUseContent: React.FC<{
  html?: string;
  isLoading?: boolean;
}> = ({ html, isLoading }) => {
  const renderedHtml = useMemo(() => {
    if (isLoading) {
      return <p style={{ textAlign: "center" }}>Loading terms of use...</p>;
    }
    if (!html) {
      return (
        <p style={{ textAlign: "center" }}>
          Terms of Use not available. Please check your connection.
        </p>
      );
    }
    return (
      <div
        className="overflow-auto h-full"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }, [html, isLoading]);

  return <>{renderedHtml}</>;
};

/**
 * 📄 Full Terms of Use Page
 * Handles fetching, caching, and displaying the Terms content.
 */
const TermsAndConditions: React.FC = () => {
  const router = useIonRouter();
  const [termsOfUse, setTermsOfUse] = useState<TermsOfUseDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleBack = () => router.goBack();

  useEffect(() => {
    const loadTermsOfUse = async () => {
      try {
        // 1️⃣ Try loading from Preferences
        const localResult = await Preferences.get({ key: TERMS_OF_USE_KEY });
        let localTerms: TermsOfUseDocument | null = null;

        if (localResult.value) {
          localTerms = JSON.parse(localResult.value);
          setTermsOfUse(localTerms);
          setIsLoading(false); // show cached version immediately
        }

        // 2️⃣ Always fetch remote content for updates
        const remoteTerms = await fetchDocumentByType("terms");

        // 3️⃣ Update if new or not stored yet
        if (!localTerms || localTerms.updated_at !== remoteTerms.updated_at) {
          await Preferences.set({
            key: TERMS_OF_USE_KEY,
            value: JSON.stringify(remoteTerms),
          });
          setTermsOfUse(remoteTerms);
        }
      } catch (error) {
        console.error("Failed to load terms of use:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTermsOfUse();
  }, []);

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle
        showUserIcon={false}
        pageTitle="Terms of Use"
        showBackButton
        onBackButtonClick={handleBack}
      />

      <IonContent className="ion-padding" style={{ "--background": "#F7F5FA" }}>
        <IonCardContent>
          <TermsOfUseContent
            html={termsOfUse?.content_html}
            isLoading={isLoading}
          />
        </IonCardContent>
      </IonContent>
    </IonPage>
  );
};

export default TermsAndConditions;
